import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import agent from "../../agent";
import { DPD_TIMEOUT_LOAD, DPD_TIMEOUT_UNLOAD, DPD_TIMEOUT_EDIT } from "../../constants/actionTypes";

const SERVICE_NAMES = {
  CALCULATOR: "Калькулятор",
  EVENT_TRACKING: "Отслеживание событий",
  LABEL_PRINT: "Печать этикеток",
  ORDER: "Заказы",
  TRACING: "Трекинг",
  TRACING2: "Трекинг 2",
};
const getServiceName = (code) => SERVICE_NAMES[code] || code;

function Dpd() {
  const dispatch = useDispatch();
  const data = useSelector((state) => state?.dpdTimeout?.data || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState({}); // { service: boolean }
  const [savedOk, setSavedOk] = useState({}); // { service: boolean }
  const [selectedServer, setSelectedServer] = useState("");

  const loadTimeouts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await agent.DpdTimeout.getServiceTimeouts();
      // Expecting array of { server, service, connectTimeoutMs, readTimeoutMs }
      dispatch({ type: DPD_TIMEOUT_LOAD, payload: res || [] });
      if (!selectedServer && Array.isArray(res) && res.length) {
        setSelectedServer(res[0].server || "");
      }
    } catch (e) {
      console.error(e);
      setError("Не удалось загрузить таймауты. Попробуйте позже.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeouts();
    return () => dispatch({ type: DPD_TIMEOUT_UNLOAD });
  }, []);

  const servers = useMemo(() => {
    const set = new Set();
    data.forEach((d) => d.server && set.add(d.server));
    return Array.from(set);
  }, [data]);

  const handleChange = (service, field, value) => {
    dispatch({ type: DPD_TIMEOUT_EDIT, payload: { service, field, value: Number(value) || 0 } });
    // clear saved status on edit
    setSavedOk((prev) => ({ ...prev, [service]: false }));
  };

  const minutesLabel = (ms) => {
    if (ms == null || isNaN(ms)) return "0 мин";
    const min = ms / 60000;
    // one decimal, but show integer if exact
    const formatted = Math.abs(min % 1) < 1e-9 ? `${Math.round(min)}` : min.toFixed(1);
    return `${formatted} мин`;
  };

  const saveRow = async (row) => {
    setSaving((prev) => ({ ...prev, [row.service]: true }));
    setError("");
    try {
      const payload = {
        connectTimeoutMs: row.connectTimeoutMs,
        readTimeoutMs: row.readTimeoutMs,
      };
      await agent.DpdTimeout.updateServiceTimeout(row.service, payload);
      setSavedOk((prev) => ({ ...prev, [row.service]: true }));
    } catch (e) {
      console.error(e);
      setError("Не удалось сохранить изменения. Попробуйте позже.");
    } finally {
      setSaving((prev) => ({ ...prev, [row.service]: false }));
    }
  };

  const refreshServer = async () => {
    if (!selectedServer) return;
    setLoading(true);
    setError("");
    try {
      await agent.DpdTimeout.refreshServiceTimeouts(selectedServer);
      await loadTimeouts();
    } catch (e) {
      console.error(e);
      setError("Не удалось обновить таймауты на сервере. Попробуйте позже.");
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Настройки таймаутов DPD</h1>
          <p style={styles.subtitle}>
            Редактируйте значения в миллисекундах. Под каждым полем отображается значение в минутах.
          </p>
        </div>
        <div style={styles.actions}>
          <div style={styles.serverSelectWrap}>
            <label htmlFor="serverSelect" style={styles.label}>Сервер:</label>
            <select
              id="serverSelect"
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              style={styles.select}
            >
              {servers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <button style={styles.refreshBtn} onClick={refreshServer} disabled={loading || !selectedServer}>
            {loading ? "Обновление..." : "Обновить таймауты на сервере"}
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {loading && !data.length ? (
        <div style={styles.loader}>Загрузка...</div>
      ) : (
        <div style={styles.grid}>
          {data
            .filter((r) => !selectedServer || r.server === selectedServer)
            .map((row) => (
              <div key={`${row.server}-${row.service}`} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <div style={styles.badge}>{row.server}</div>
                    <h3 style={styles.serviceName}>{getServiceName(row.service)}</h3>
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>connectTimeoutMs (мс)</label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={row.connectTimeoutMs ?? 0}
                    onChange={(e) => handleChange(row.service, "connectTimeoutMs", e.target.value)}
                    style={styles.input}
                  />
                  <div style={styles.hint}>{minutesLabel(row.connectTimeoutMs)}</div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.fieldLabel}>readTimeoutMs (мс)</label>
                  <input
                    type="number"
                    min={0}
                    step={1000}
                    value={row.readTimeoutMs ?? 0}
                    onChange={(e) => handleChange(row.service, "readTimeoutMs", e.target.value)}
                    style={styles.input}
                  />
                  <div style={styles.hint}>{minutesLabel(row.readTimeoutMs)}</div>
                </div>

                <div style={styles.cardFooter}>
                  <button
                    style={{
                      ...styles.saveBtn,
                      ...(saving[row.service] ? styles.saveBtnDisabled : {}),
                    }}
                    onClick={() => saveRow(row)}
                    disabled={!!saving[row.service]}
                  >
                    {saving[row.service] ? "Сохранение..." : savedOk[row.service] ? "Сохранено" : "Сохранить"}
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    padding: "24px",
    maxWidth: 1100,
    margin: "0 auto",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    color: "#0f172a",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 24,
    fontWeight: 700,
  },
  subtitle: {
    margin: "6px 0 0",
    color: "#475569",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  serverSelectWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "6px 10px",
  },
  label: {
    fontSize: 13,
    color: "#334155",
  },
  select: {
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: 14,
    color: "#0f172a",
  },
  refreshBtn: {
    background: "#0ea5e9",
    color: "white",
    border: "none",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },
  card: {
    background: "white",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  badge: {
    display: "inline-block",
    background: "#f1f5f9",
    color: "#0f172a",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    marginBottom: 8,
  },
  serviceName: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
  },
  fieldGroup: {
    marginTop: 8,
  },
  fieldLabel: {
    display: "block",
    fontSize: 12,
    color: "#64748b",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    outline: "none",
    fontSize: 14,
  },
  hint: {
    fontSize: 12,
    color: "#475569",
    marginTop: 6,
  },
  cardFooter: {
    marginTop: 12,
    display: "flex",
    justifyContent: "flex-end",
  },
  saveBtn: {
    background: "#22c55e",
    color: "white",
    border: "none",
    padding: "10px 14px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
  },
  saveBtnDisabled: {
    opacity: 0.7,
    cursor: "default",
  },
  loader: {
    padding: 24,
    textAlign: "center",
    color: "#475569",
  },
  error: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
};

export default Dpd;